import React, { useMemo, useState } from 'react'
import {
  childrenItemViewLabels,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '../variable/spaceTime.data'
import styled from 'styled-components'
import { Layer } from '../variable/spaceTime.types'
import ViewSelectModalVariable from './viewSelectModalVariable.component'
import ViewSelectModalSpace from './viewSelectModalSpace.component'
import ViewSelectModalTime from './viewSelectModalTime.component'
import { VariableAndOptionalResult, VariableWithConclusion } from '../variable/variables.typing'
import { ReportWithLayer } from './reports'
import { Expression } from '../../prix/types/expression'
import { ExpressionOperationAvailable } from '../../prix/types/expression'
import { ToolsAndOptionalResult } from '../tools/tools.typing'
import { VariableCollectionsAndOptionalResult } from '../variableCollection/variableCollection.typing'
import { extractStringEqualityValue } from './extractStringEqualityValue.util'
import { formatDateUTC } from '~/prix/types/date'

const ViewSelectDesktopBoxWrapper = styled.div<{ $isDisabled: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: row;

  small,
  span,
  h2 {
    color: #363636;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    gap: 7px;
  }

  button {
    display: flex;
    flex-direction: column;
    background: transparent;
    box-shadow: none;
    border: none;
    border-radius: 0;
    padding: 1rem;
    cursor: pointer;
    justify-content: flex-start;
    transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
    transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
    transition-property: background-color, box-shadow;
    text-align: left;

    border-bottom: 1px solid #cacaca;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
    transition: opacity 0.3s ease;

    &:hover {
      background-color: #fff;
      box-shadow: ${({ theme }) => theme.shadow(2)};
      z-index: 1;
    }

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    &:not(:first-child):not(:last-child) {
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    h2 {
      font-size: 0.7rem;
      font-weight: 700;
      margin: 0;
      text-transform: uppercase;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      background: white;
      border: none !important;
      border-radius: 8px;
      padding: 0.75rem;
      gap: 4px;
      box-shadow: ${({ theme }) => theme.shadow(1)};
      line-height: 0.8rem;

      h2 {
        font-size: 0.65rem;
      }
      span {
        font-size: 0.9rem;
      }
      small {
        font-size: 0.8rem;
        color: #606060;
        display: none;
      }
    }
  }

  .column {
    flex: 1;
  }
`

const ageGroupKeysAndLabels = {
  startup: 'Startup (0 a 2 anos)',
  young: 'Jovem (2 a 5 anos)',
  growth: 'Crescimento (5 a 10 anos)',
  established: 'Estabelecida (mais de 10 anos)',
}

export function extractAppliedFilters(
  filter: Expression | undefined,
  filterableBy?: any,
): string[] {
  if (!filter || !filterableBy?.shape) return []
  const filters: string[] = []

  const propertiesKeys = Object.keys(filterableBy.shape || {})
  const possibleProperties = propertiesKeys.map(key => ({
    value: {
      ...filterableBy.shape[key],
      ...(key === 'ageGroup' ? { keysAndLabels: ageGroupKeysAndLabels } : {}),
      key: key as string,
    },
    label: filterableBy.shape[key].currentMeta?.label || key,
  }))

  const getBooleanLabels = () => [
    { value: { key: 'true', kind: 'boolean', type: 'boolean' }, label: 'Verdadeiro' },
    { value: { key: 'false', kind: 'boolean', type: 'boolean' }, label: 'Falso' },
  ]

  const checkOperationsAvailable = (operation: 'number' | 'string' | 'boolean' | undefined) => {
    return (
      ExpressionOperationAvailable[operation as keyof typeof ExpressionOperationAvailable] || []
    )
  }

  const extractFilterLabel = (expr: Expression): string | null => {
    if (expr.kind !== 'binary' || expr.left?.kind !== 'property') return null

    const propertyKey = expr.left.property as any

    const propertyInfo = possibleProperties.find(prop => prop.value.key === propertyKey)
    if (!propertyInfo) return null

    const possibleOperators = checkOperationsAvailable(propertyInfo.value.type as any)
    const operatorInfo = possibleOperators.find((op: any) => op?.value?.key === expr.operator)
    if (!operatorInfo) {
      return null
    }
    const operatorLabel = operatorInfo.label

    let valueLabel = ''
    if (expr.right?.kind === 'literal') {
      const rightValue = expr.right?.label ?? expr.right.value

      if (expr.right.subType === 'enumerated' && propertyInfo.value.keysAndLabels) {
        valueLabel = propertyInfo.value.keysAndLabels[rightValue as string] || String(rightValue)
      } else if (expr.right.subType === 'boolean') {
        const booleanLabels = getBooleanLabels()
        const booleanInfo = booleanLabels.find(option => option.value.key === String(rightValue))
        valueLabel = booleanInfo?.label || String(rightValue)
      } else {
        valueLabel = String(rightValue)
      }
    }

    if (expr.operator === '=') {
      if (propertyKey === 'agentPolygonCpf') {
        return valueLabel + ' (POLÍGONO)'
      }
      return valueLabel
    } else {
      return operatorLabel + ' ' + valueLabel
    }
  }

  function processExpression(expr: Expression) {
    if (expr.kind === 'binary') {
      if (expr.operator === 'AND' || expr.operator === 'OR') {
        processExpression(expr.left)
        processExpression(expr.right)
      } else {
        const filterLabel = extractFilterLabel(expr)
        if (filterLabel) {
          filters.push(filterLabel)
        }
      }
    } else if (expr.kind === 'list') {
      expr.items?.forEach(item => processExpression(item))
    }
  }

  processExpression(filter)

  return [...new Set(filters)]
}

export interface ViewSelectTopBoxDesktopProps {
  variables: VariableWithConclusion[]
  layer: Layer
  isFromVariableCollection?: boolean
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  onChangeReport: (report: ReportWithLayer | null) => void
  stateId: number | null
  stateName: string | null
  subtitle?: string | null
  isDisabled: boolean
}

export default function ViewSelectTopBoxDesktop({
  layer,
  variables,
  onChangeLayer,
  onChangeReport,
  stateId,
  stateName,
  subtitle,
  isDisabled,
}: ViewSelectTopBoxDesktopProps) {
  const [isModalVariableOpen, setIsModalVariableOpen] = useState(false)
  const [isModalTimeOpen, setIsModalTimeOpen] = useState(false)
  const [isModalSpaceOpen, setIsModalSpaceOpen] = useState(false)
  const editableLayer = layer
  const editableVariable = editableLayer.variable as
    | ToolsAndOptionalResult
    | VariableAndOptionalResult
    | VariableCollectionsAndOptionalResult
  const isVariableCollectionView = Boolean(
    (editableVariable as VariableCollectionsAndOptionalResult)?.isVariableCollection,
  )

  const selectedAgentPolygonCpf = useMemo(() => {
    return extractStringEqualityValue(
      (editableVariable as VariableAndOptionalResult)?.filter,
      'agentPolygonCpf',
    )
  }, [(editableVariable as VariableAndOptionalResult)?.filter])

  const appliedFilters = useMemo(() => {
    return extractAppliedFilters(
      (editableVariable as VariableAndOptionalResult)?.filter,
      (editableVariable as VariableAndOptionalResult)?.filterableBy,
    )
  }, [
    (editableVariable as VariableAndOptionalResult)?.filter,
    (editableVariable as VariableAndOptionalResult)?.filterableBy,
    editableVariable.key,
    editableVariable.presetKey,
  ])

  const timeLabel = useMemo(() => {
    if (layer.time.type === 'customDate' && layer.time.date) {
      return formatDateUTC(layer.time.date, 'dd/MM/yyyy')
    }

    if (
      (layer.time.type === 'customDateRange' || layer.time.type === 'customDateRangePredicted') &&
      'startDate' in layer.time &&
      'endDate' in layer.time &&
      layer.time.startDate &&
      layer.time.endDate
    ) {
      return `${formatDateUTC(layer.time.startDate, 'dd/MM/yyyy')} a ${formatDateUTC(layer.time.endDate, 'dd/MM/yyyy')}`
    }

    return null
  }, [layer.time])

  const shouldHideTimeLabel = useMemo(() => {
    if (!timeLabel) return false

    const normalizedTimeTitle = layer.time.title.replace(/\s+/g, ' ').trim()
    const normalizedTimeLabel = timeLabel.replace(/\s+/g, ' ').trim()

    return normalizedTimeTitle.includes(normalizedTimeLabel)
  }, [layer.time.title, timeLabel])

  const isGeneralLabelOnly = layer.variable.label?.trim().toLowerCase() === 'geral'
  const hasLabel = Boolean(layer.variable.label) && !isGeneralLabelOnly
  const hasTitle = Boolean(layer.variable.title)
  const mainVariableLabel = hasLabel
    ? layer.variable.label!
    : layer.variable.title || layer.variable.label || ''
  const secondaryVariableTitle =
    hasLabel && hasTitle && layer.variable.title !== layer.variable.label
      ? layer.variable.title
      : null

  let spaceChildrenLabel: string
  if (layer.space.childrenType === 'custom') {
    spaceChildrenLabel = layer.space.childrenTitle
  } else if (layer.space.childrenType === 'item') {
    spaceChildrenLabel =
      childrenItemViewLabels[layer.space.childrenItemView! as keyof typeof childrenItemViewLabels]
  } else if (layer.space.childrenType === layer.space.delimitationType) {
    spaceChildrenLabel = spaceTypeSingularLabels[layer.space.childrenType]
  } else {
    spaceChildrenLabel = spaceTypePluralLabels[layer.space.childrenType]
  }

  const restrictColumn = (
    editableVariable as ToolsAndOptionalResult | VariableCollectionsAndOptionalResult
  )?.restrictView

  const subtitleSplitted = useMemo(() => subtitle?.split('-').pop(), [subtitle])

  return (
    <React.Fragment>
      <ViewSelectDesktopBoxWrapper $isDisabled={isDisabled}>
        {!restrictColumn?.includes('variable') && (
          <button
            className='column variable-characteristic'
            onClick={() => {
              if (isDisabled) return
              setIsModalVariableOpen(true)
            }}
          >
            <h2>O quê{editableVariable.key === 'overview' ? ' (visão geral)' : null}</h2>
            <span>{mainVariableLabel}</span>
            {appliedFilters.length > 0 ? (
              <small>Filtros: {appliedFilters.join(', ')}</small>
            ) : (
              <small>{secondaryVariableTitle}</small>
            )}
          </button>
        )}
        {!restrictColumn?.includes('time') && !isVariableCollectionView && (
          <button
            className='column time'
            onClick={() => {
              if (isDisabled) return
              setIsModalTimeOpen(true)
            }}
          >
            <h2>Quando</h2>
            <span>{layer.time.title}</span>
            {!shouldHideTimeLabel && <small>{timeLabel}</small>}
          </button>
        )}
        {!restrictColumn?.includes('space') && (
          <button
            className='column space'
            onClick={() => {
              if (isDisabled) return
              setIsModalSpaceOpen(true)
            }}
          >
            <h2>Onde</h2>
            <span>
              {layer.space.delimitationTitle}{' '}
              {layer.space.delimitationType === 'city' && subtitleSplitted !== undefined
                ? `- ${subtitleSplitted}`
                : null}
            </span>
            <small>{spaceChildrenLabel}</small>
          </button>
        )}
      </ViewSelectDesktopBoxWrapper>

      {!restrictColumn?.includes('variable') && (
        <ViewSelectModalVariable
          variables={variables}
          isOpen={isModalVariableOpen}
          onClose={() => setIsModalVariableOpen(false)}
          layer={layer}
          baseLayer={editableLayer}
          onChangeLayer={onChangeLayer}
          stateId={stateId}
          stateName={stateName}
        />
      )}

      {!restrictColumn?.includes('space') && (
        <ViewSelectModalSpace
          layer={editableLayer}
          isOpen={isModalSpaceOpen}
          onClose={() => setIsModalSpaceOpen(false)}
          onChangeLayer={onChangeLayer}
        />
      )}

      {!restrictColumn?.includes('time') && !isVariableCollectionView && (
        <ViewSelectModalTime
          layer={editableLayer}
          isOpen={isModalTimeOpen}
          onClose={() => setIsModalTimeOpen(false)}
          onChangeLayer={onChangeLayer}
          onChangeReport={onChangeReport}
        />
      )}
    </React.Fragment>
  )
}
